// ============================================================================
// DOC TEMPLATES REACT QUERY HOOKS
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { docTemplatesApi } from '@/lib/api/client'
import { useSessionStore } from '@/stores/sessionStore'
import type {
  DocTemplateResponse,
  CreateDocTemplateRequest,
  UpdateDocTemplateConfigRequest,
  DocConfigurableItemsResponse,
  SectionTemplate,
  CreateSectionTemplateRequest,
} from '@ispot/shared'

// ============================================================================
// QUERY KEYS
// ============================================================================

export const docTemplatesKeys = {
  all: (accountId: string) => ['docTemplates', accountId] as const,
  lists: (accountId: string) => [...docTemplatesKeys.all(accountId), 'list'] as const,
  detail: (accountId: string, templateId: string) => [...docTemplatesKeys.all(accountId), templateId] as const,
  configurableItems: (accountId: string) => [...docTemplatesKeys.all(accountId), 'configurable'] as const,
  sectionTemplates: (accountId: string) => [...docTemplatesKeys.all(accountId), 'sections'] as const,
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get all doc templates for current account
 */
export function useDocTemplates() {
  const { accountId } = useSessionStore()

  return useQuery({
    queryKey: docTemplatesKeys.lists(accountId || ''),
    queryFn: async () => {
      if (!accountId) return []
      const response = await docTemplatesApi.list(accountId)
      return response.templates
    },
    staleTime: 60000, // 1 minute
    enabled: !!accountId,
  })
}

/**
 * Get a single doc template by ID
 */
export function useDocTemplate(templateId: string | null) {
  const { accountId } = useSessionStore()

  return useQuery({
    queryKey: docTemplatesKeys.detail(accountId || '', templateId || ''),
    queryFn: async () => {
      if (!accountId || !templateId) return null
      const response = await docTemplatesApi.get(accountId, templateId)
      return response.template
    },
    staleTime: 60000,
    enabled: !!accountId && !!templateId,
  })
}

/**
 * Get configurable items (section templates + entities) for drag & drop
 */
export function useConfigurableItems() {
  const { accountId } = useSessionStore()

  return useQuery({
    queryKey: docTemplatesKeys.configurableItems(accountId || ''),
    queryFn: async () => {
      if (!accountId) return { sectionTemplates: [], entities: [] }
      return await docTemplatesApi.getConfigurableItems(accountId)
    },
    staleTime: 5 * 60000, // 5 minutes (relatively static data)
    enabled: !!accountId,
  })
}

/**
 * Get section templates
 */
export function useSectionTemplates() {
  const { accountId } = useSessionStore()

  return useQuery({
    queryKey: docTemplatesKeys.sectionTemplates(accountId || ''),
    queryFn: async () => {
      if (!accountId) return []
      const response = await docTemplatesApi.listSectionTemplates(accountId)
      return response.sections
    },
    staleTime: 60000,
    enabled: !!accountId,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new doc template
 */
export function useCreateDocTemplate() {
  const { accountId } = useSessionStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateDocTemplateRequest) => {
      if (!accountId) throw new Error('No account selected')
      const response = await docTemplatesApi.create(accountId, data)
      return response.template
    },
    onSuccess: () => {
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: docTemplatesKeys.lists(accountId) })
      }
    },
  })
}

/**
 * Update template configuration
 */
export function useUpdateDocTemplateConfig() {
  const { accountId } = useSessionStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      templateId,
      config,
    }: {
      templateId: string
      config: UpdateDocTemplateConfigRequest
    }) => {
      if (!accountId) throw new Error('No account selected')
      const response = await docTemplatesApi.updateConfig(accountId, templateId, config)
      return response.template
    },
    onSuccess: (_, variables) => {
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: docTemplatesKeys.detail(accountId, variables.templateId) })
        queryClient.invalidateQueries({ queryKey: docTemplatesKeys.lists(accountId) })
      }
    },
  })
}

/**
 * Delete a doc template
 */
export function useDeleteDocTemplate() {
  const { accountId } = useSessionStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (templateId: string) => {
      if (!accountId) throw new Error('No account selected')
      return await docTemplatesApi.delete(accountId, templateId)
    },
    onSuccess: () => {
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: docTemplatesKeys.lists(accountId) })
      }
    },
  })
}

/**
 * Create a new section template
 */
export function useCreateSectionTemplate() {
  const { accountId } = useSessionStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateSectionTemplateRequest) => {
      if (!accountId) throw new Error('No account selected')
      const response = await docTemplatesApi.createSectionTemplate(accountId, data)
      return response.section
    },
    onSuccess: () => {
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: docTemplatesKeys.sectionTemplates(accountId) })
        queryClient.invalidateQueries({ queryKey: docTemplatesKeys.configurableItems(accountId) })
      }
    },
  })
}

/**
 * Create a document from a template
 */
export function useCreateDocFromTemplate() {
  const { accountId, projectId } = useSessionStore()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      templateId,
      title,
    }: {
      templateId: string
      title: string
    }) => {
      if (!accountId || !projectId) throw new Error('No account or project selected')
      return await docTemplatesApi.createDocFromTemplate(accountId, projectId, templateId, { title })
    },
    onSuccess: () => {
      // Invalidate docs list (when we implement it)
      if (accountId && projectId) {
        queryClient.invalidateQueries({ queryKey: ['artifacts', accountId, projectId] })
      }
    },
  })
}
